import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Profile } from 'src/app/core/schema/Profile.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { StatusDotComponent } from 'src/app/ui-kit/status-dot/status-dot.component';

/**
 * Dense list-row for a team {@link Profile}. Used in team admin lists.
 *
 * @security FERPA — email and notes are PII.
 *
 * @feature shared
 */
@Component({
  selector: 'app-staff-member-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, IconComponent, StatusDotComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open ' + displayName() + ' staff profile'"
    >
      <app-avatar [name]="displayName()" size="sm" />

      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-body">{{ displayName() }}</p>
        @if (subtitle()) {
          <p class="truncate text-xs text-muted">{{ subtitle() }}</p>
        }
      </div>

      <app-status-dot [status]="statusDot()" [ariaLabel]="statusLabel()" />

      @if (clickable()) {
        <app-icon name="chevron-forward" size="xs" color="medium" />
      }
    </button>
  `,
})
export class StaffMemberListItemComponent {
  /** The profile to render. @security FERPA. */
  readonly profile = input.required<Profile>();

  /** Optional display name override (e.g., a joined User.firstName + lastName). */
  readonly nameOverride = input<string | undefined>(undefined);

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the row is clicked. */
  readonly rowClick = output<Profile>();

  readonly displayName = computed(() => this.nameOverride() || this.profile().email || 'Unknown');

  readonly subtitle = computed(() => {
    const p = this.profile();
    const parts: string[] = [];
    if (p.role) parts.push(p.role);
    if (!this.nameOverride() && p.email) {
      // email already shown as display name
    } else if (p.email) {
      parts.push(p.email);
    }
    return parts.join(' · ');
  });

  readonly statusDot = computed(() =>
    this.profile().isActive === false ? ('off-track' as const) : ('on-track' as const),
  );

  readonly statusLabel = computed(() => (this.profile().isActive === false ? 'Inactive' : 'Active'));

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.profile());
    }
  }
}
